/**
 * Utility functions for pixel-density and scale-factor calculations.
 * All functions are pure with no side effects.
 */

/**
 * Calculates pixels per inch (PPI) for a display.
 *
 * @param resolutionW  Horizontal native resolution in physical pixels
 * @param resolutionH  Vertical native resolution in physical pixels
 * @param diagonalInches  Physical diagonal size of the display in inches
 * @returns PPI as a number
 */
export function calculatePpi(
  resolutionW: number,
  resolutionH: number,
  diagonalInches: number
): number {
  if (diagonalInches <= 0) return 0;
  return Math.sqrt(resolutionW ** 2 + resolutionH ** 2) / diagonalInches;
}

/**
 * Calculates the CSS `scale()` transform factor to apply to the preview
 * container so that its UI elements appear at their true physical size
 * on the user's actual screen.
 *
 * Derivation:
 *   1 logical point on target = targetScalingFactor / targetPPI physical inches.
 *   1 CSS px on user's screen  = userScalingFactor / userPPI physical inches,
 *     where userScalingFactor  = nativeResolutionW / window.screen.width
 *     (the real ratio of hardware pixels to CSS pixels on the user's display).
 *
 *   previewScale = (targetScalingFactor / targetPPI) / (userScalingFactor / userPPI)
 *                = (userPPI × targetScalingFactor) / (targetPPI × userScalingFactor)
 *
 *   Example — user: MacBook Pro 14" (254 PPI, Default → SF ≈ 2)
 *             target: same 14" MBP (254 PPI, SF = 2)
 *             previewScale = (254 × 2) / (254 × 2) = 1.0 (true to size) ✓
 *
 * NOTE: On macOS Retina displays, `window.devicePixelRatio` is always 2
 * regardless of the active scaling mode (More Space / Default / Larger Text).
 * The real scaling factor must be derived from `nativeResolutionW / screen.width`
 * to get accurate physical sizing. The caller (useScaleFactor) handles this.
 *
 * @param userPpi             PPI of the user's physical display
 * @param targetPpi           PPI of the monitor being previewed
 * @param targetScalingFactor Apple HiDPI scaling factor on the target monitor
 * @param userScalingFactor   Real CSS-to-native scaling on the user's display
 *                            (nativeResolutionW / window.screen.width)
 * @returns CSS scale factor (dimensionless)
 */
export function calculatePreviewScale(
  userPpi: number,
  targetPpi: number,
  targetScalingFactor: number,
  userScalingFactor: number
): number {
  if (userPpi <= 0 || targetPpi <= 0 || userScalingFactor <= 0) return 1;
  return (userPpi * targetScalingFactor) / (targetPpi * userScalingFactor);
}

/**
 * Derives the logical (HiDPI) resolution for a display given its native
 * resolution and the active Apple scaling factor.
 *
 * @param nativeW       Native horizontal resolution in physical pixels
 * @param nativeH       Native vertical resolution in physical pixels
 * @param scalingFactor Apple HiDPI scaling factor (e.g. 2 for Default Retina)
 * @returns `{ logicalW, logicalH }` in logical (point) pixels
 */
export function calculateLogicalResolution(
  nativeW: number,
  nativeH: number,
  scalingFactor: number
): { logicalW: number; logicalH: number } {
  if (scalingFactor <= 0) return { logicalW: nativeW, logicalH: nativeH };
  return {
    logicalW: Math.round(nativeW / scalingFactor),
    logicalH: Math.round(nativeH / scalingFactor),
  };
}
